import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/Toast";
import { useContextMenu } from "../../components/Menu";
import Icon from "../../components/Icon";
import { BackBar, Page } from "../../components/Band";
import { Chip, ClubLogo, Content } from "../../components/primitives";
import { useBioPhotos } from "./useBioPhotos";
import { ADMIN_TABS, manageHref } from "./manageUtils";
import OverviewTab from "./OverviewTab";
import EditBioTab from "./EditBioTab";
import EventsTab from "./events/EventsTab";
import MembersTab from "./MembersTab";
import ApplicationsTab from "./ApplicationsTab";
import ShareTab from "./ShareTab";

/**
 * Everything that belongs to ONE club on /manage. ManagePage renders it with key={club.id}, so a club
 * switch (or leaving the page) unmounts the whole photo session, tab state and cached lists, and the
 * next club starts fresh (AdminScreen :823).
 */
export default function ClubManager({ club, clubs, tab, onRefresh, onPatchClub, onTransferred }) {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user } = useAuth();
  const { menu, openMenu } = useContextMenu();
  const [bioSaving, setBioSaving] = useState(false);

  const go = useCallback((clubId, nextTab, state) => navigate(manageHref(clubId, nextTab), state ? { state } : undefined), [navigate]);
  const onPatch = useCallback((fields) => onPatchClub(club.id, fields), [club.id, onPatchClub]);
  const notify = useCallback((m, icon) => toast(m, icon), [toast]);
  const photos = useBioPhotos({ uid: user?.uid, clubId: club.id, savedGallery: club.gallery, notify });

  // Overview "Add Photos" tile: pick + upload first, then open Edit Bio at the gallery (AdminScreen :1683).
  const addPhotosFromOverview = useCallback(async () => {
    const saved = Array.isArray(club.gallery) ? club.gallery.filter((u) => typeof u === "string" && u) : [];
    const picked = await photos.pickPhotos(saved);
    if (picked) go(club.id, "editbio", { keepStaged: true });
  }, [club, photos, go]);

  const multi = clubs.length > 1;
  const openSwitcher = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    openMenu(
      Math.max(8, r.right - 260),
      r.bottom + 6,
      clubs.map((c) => ({ icon: c.id === club.id ? "check" : undefined, label: c.name, testId: `manage.switch.${c.id}`, run: () => go(c.id, tab) }))
    );
  };

  const switcher = (
    <Chip
      glass
      className="mg-switch"
      data-testid="manage.switchClub"
      aria-label={multi ? `Switch club, current: ${club.name}` : club.name}
      aria-haspopup={multi ? "menu" : undefined}
      onClick={multi ? openSwitcher : undefined}
      disabled={bioSaving}
      style={multi ? undefined : { cursor: "default" }}
    >
      <ClubLogo club={club} size={26} />
      <span className="hide-sm">{club.name}</span>
      {multi && <Icon name="down" size={14} />}
    </Chip>
  );

  const body = {
    overview: <OverviewTab club={club} onTab={(t) => go(club.id, t)} onAddPhotos={addPhotosFromOverview} onPatch={onPatch} />,
    editbio: (
      <EditBioTab
        club={club}
        photos={photos}
        keepStaged={!!location.state?.keepStaged}
        onPatch={onPatch}
        onSavingChange={setBioSaving}
        onClose={() => go(club.id, "overview")}
      />
    ),
    events: <EventsTab club={club} onClubChanged={onRefresh} />,
    users: <MembersTab club={club} onTransferred={onTransferred} />,
    applications: <ApplicationsTab club={club} />,
    share: <ShareTab club={club} />,
  }[tab];

  return (
    <Page>
      <BackBar title="Manage Club" right={switcher} testId="manage" />
      <Content>
        <div className="dock" role="tablist" style={{ marginBottom: 20 }}>
          {ADMIN_TABS.map(([k, icon, label]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} data-testid={`manage.tab.${k}`} disabled={bioSaving} className={tab === k ? "on" : ""} onClick={() => go(club.id, k)}>
              <Icon name={icon} size={16} />{label}
            </button>
          ))}
        </div>
        {body}
      </Content>
      {menu}
    </Page>
  );
}
